import { P, FF } from "../../styles/tokens";
import { fmt, sum, sumFijosReal } from "../../utils/format";
import { MESES } from "../../constants/months";
import { catIcon, catColor } from "../../constants/categories";
import { getMsiCuotasMes } from "../../utils/gastos";
import { Card, Heading } from "../ui";
import { useFinancial } from "../../contexts/FinancialContext";
import { useNavigation } from "../../contexts/NavigationContext";
import { useUI } from "../../contexts/UIContext";
import { isRecurrenteGhost } from "../../hooks/useMonthData";

export default function AnalisisView() {
  const {
    data, month, totalIng, totalFij, totalExt, ahorroReal, flujoNeto,
    msiCompras, cuotasMes, totalMsiMes, presupuestos,
    comprometido, flujoDisponible, year,
    totalDeudaTDCGlobal, creditoIds,
  } = useFinancial();
  const { activeMonth, setSection } = useNavigation();
  const { mobile } = useUI();

  // Mes anterior
  const prev = activeMonth > 0 ? data[activeMonth - 1] : null;
  const prevIng = prev ? sum(prev.ingresos) : 0;
  const prevFij = prev ? sumFijosReal(prev.fijos) : 0;
  const prevExt = prev ? sum(prev.extras) : 0;
  const prevAhorro = prev ? (prev.ahorroMetas || []).reduce((s, a) => s + (a.monto || 0), 0) : 0;
  const prevGasto = prevFij + prevExt;
  const totalGasto = totalFij + totalExt;
  const prevFlujoNeto = prevIng - prevGasto - prevAhorro;
  const hasPrev = activeMonth > 0 && prevIng > 0;

  // Extras reales por categoría
  const extrasReales = month.extras.filter(e => !isRecurrenteGhost(e) && e.monto > 0);
  const bycat = {};
  extrasReales.forEach(e => { bycat[e.cat || "Otros"] = (bycat[e.cat || "Otros"] || 0) + (e.monto || 0); });
  const catEntries = Object.entries(bycat).sort((a, b) => b[1] - a[1]);
  const totalExtReales = extrasReales.reduce((s, e) => s + e.monto, 0);

  // Categorías mes anterior
  const prevBycat = {};
  if (prev) prev.extras.filter(e => !isRecurrenteGhost(e) && e.monto > 0).forEach(e => {
    prevBycat[e.cat || "Otros"] = (prevBycat[e.cat || "Otros"] || 0) + (e.monto || 0);
  });

  // Presupuestos
  const bycatGasto = {};
  extrasReales.forEach(e => { if (e.cat) bycatGasto[e.cat] = (bycatGasto[e.cat] || 0) + (e.monto || 0); });

  // Semanas del mes
  const diasMes = new Date(year, activeMonth + 1, 0).getDate();
  const today = new Date();
  const todayDay = activeMonth === today.getMonth() && year === today.getFullYear() ? today.getDate() : diasMes;
  const diasRestantes = Math.max(diasMes - todayDay, 1);
  const dineroLibre = Math.max(flujoDisponible, 0);
  const puedesHoy = diasRestantes > 0 ? Math.round(dineroLibre / diasRestantes) : 0;
  const porSemana = Math.round(dineroLibre / (diasMes / 7));
  const porDia = Math.round(dineroLibre / diasMes);

  // Gastos por semana
  const semanaGastos = [0, 0, 0, 0, 0];
  extrasReales.forEach(e => {
    if (!e.fecha) return;
    const d = new Date(e.fecha + "T00:00:00").getDate();
    const sem = Math.min(Math.ceil(d / 7) - 1, 4);
    semanaGastos[sem] += e.monto;
  });
  const numSemanas = Math.ceil(diasMes / 7);

  // Tendencia 3 meses
  const trend = [];
  for (let i = Math.max(activeMonth - 2, 0); i <= activeMonth; i++) {
    const m = data[i];
    if (!m) continue;
    const ing = sum(m.ingresos);
    const gasto = sumFijosReal(m.fijos) + sum(m.extras);
    const ahorro = (m.ahorroMetas || []).reduce((s, a) => s + (a.monto || 0), 0);
    trend.push({ mes: MESES[i], ing, gasto, ahorro, idx: i });
  }

  // Delta helper
  const Delta = ({ val, prev: p, inverse = false }) => {
    if (!hasPrev || p === undefined) return null;
    const diff = val - p;
    if (diff === 0) return <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}> = igual</span>;
    const isGood = inverse ? diff < 0 : diff > 0;
    return (
      <span style={{ fontSize: 10, fontWeight: 700, color: isGood ? "#059669" : "#DC2626", fontFamily: FF }}>
        {" "}{diff > 0 ? "▲" : "▼"} {fmt(Math.abs(diff))}
      </span>
    );
  };

  // Insights
  const insights = [];
  if (catEntries.length > 0 && hasPrev) {
    let maxDelta = null, maxDeltaVal = 0;
    catEntries.forEach(([cat, val]) => {
      const p = prevBycat[cat] || 0;
      const d = val - p;
      if (d > maxDeltaVal) { maxDelta = cat; maxDeltaVal = d; }
    });
    if (maxDelta) insights.push(`${catIcon(maxDelta)} ${maxDelta} subió ${fmt(maxDeltaVal)} vs ${MESES[activeMonth - 1]}.`);
  }
  if (puedesHoy > 0) insights.push(`💡 Puedes gastar máximo ${fmt(puedesHoy)}/día para llegar bien al fin de mes.`);
  if (msiCompras.length > 0) {
    const sorted = [...msiCompras].sort((a, b) => (a.mesInicio + a.meses) - (b.mesInicio + b.meses));
    const next = sorted[0];
    if (next) {
      const finMes = (next.mesInicio + next.meses - 1) % 12;
      insights.push(`💳 En ${MESES[finMes]} se libera tu MSI de "${next.concepto}" (-${fmt(Math.round(next.totalCompra / next.meses))}/mes).`);
    }
  }
  if (totalIng > 0 && totalGasto / totalIng > 0.8) insights.push("⚠️ Llevas más del 80% de tu ingreso gastado.");
  if (flujoNeto < 0) insights.push("🔴 Tu flujo neto es negativo. Estás gastando más de lo que ganas.");

  // Insight: Debt-to-Income
  if (totalIng > 0) {
    const dti = ((totalDeudaTDCGlobal || 0) + totalFij) / totalIng;
    if (dti > 0.5) insights.push(`🔴 Tu deuda es el ${Math.round(dti * 100)}% de tu ingreso. Lo saludable es menos del 36%.`);
    else if (dti > 0.36) insights.push(`🟡 Tu deuda es el ${Math.round(dti * 100)}% de tu ingreso. Intenta bajarla al 36% o menos.`);
  }

  // Insight: Frecuencia TDC vs débito
  const totalExtrasCount = extrasReales.length;
  const tdcExtrasCount = extrasReales.filter(e => e.cuentaId && creditoIds && creditoIds.has(e.cuentaId)).length;
  if (totalExtrasCount > 5 && tdcExtrasCount > 0) {
    const pctTDC = Math.round((tdcExtrasCount / totalExtrasCount) * 100);
    if (pctTDC > 50) insights.push(`💳 El ${pctTDC}% de tus compras son con tarjeta de crédito. Vigila no acumular deuda.`);
  }

  // Insight: Gasto hormiga
  const hormigaByCat = {};
  extrasReales.forEach(e => {
    if (e.monto > 0 && e.monto < 200) {
      const cat = e.cat || "Otros";
      if (!hormigaByCat[cat]) hormigaByCat[cat] = { count: 0, total: 0 };
      hormigaByCat[cat].count++;
      hormigaByCat[cat].total += e.monto;
    }
  });
  Object.entries(hormigaByCat).forEach(([cat, { count, total }]) => {
    if (count >= 5) insights.push(`🐜 ${count} compras chicas en ${cat} suman ${fmt(total)}. Los gastos hormiga se acumulan.`);
  });

  // Insight: Recurrente no registrado
  if (activeMonth > 0) {
    const prevExtras = (data[activeMonth - 1]?.extras || []).filter(e => !isRecurrenteGhost(e) && e.concepto);
    const currConceptos = new Set(extrasReales.map(e => e.concepto?.toLowerCase()).filter(Boolean));
    const repetidos = prevExtras.filter(e => currConceptos.has(e.concepto?.toLowerCase()));
    if (repetidos.length > 0) {
      const nombres = [...new Set(repetidos.map(e => e.concepto))].slice(0, 3).join(", ");
      insights.push(`🔁 "${nombres}" aparece como extra en 2 meses. ¿Debería ser recurrente?`);
    }
  }

  // Empty state: sin ingresos ni gastos suficientes para analizar
  if (totalIng === 0 && totalGasto === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 24px", textAlign: "center", background: P.ingresos.pastel, borderRadius: 20, border: `1.5px solid ${P.ingresos.tint}` }}>
        <div style={{ fontSize: 48, marginBottom: 14, lineHeight: 1 }}>📈</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 8 }}>Aún no hay datos para analizar</div>
        <div style={{ fontSize: 13, color: P.textMd, fontFamily: FF, lineHeight: 1.6, maxWidth: 280 }}>
          Registrá tus primeros gastos e ingresos para ver tus tendencias, presupuestos y proyecciones aquí.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ═══ 1. RESUMEN CON DELTA ═══ */}
      <Card style={{ padding: mobile ? "16px 18px" : "20px 24px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: P.textSm, fontFamily: FF, marginBottom: 12 }}>
          {MESES[activeMonth]} {year}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[
            { label: "Ingresaste", val: totalIng, prev: prevIng, color: P.ingresos.base },
            { label: "Gastaste", val: totalGasto, prev: prevGasto, color: P.extras.base, inverse: true },
            { label: "Ahorraste", val: ahorroReal, prev: prevAhorro, color: "#10B981" },
          ].map(c => (
            <div key={c.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: P.textXs, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: FF }}>{c.label}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: c.color, fontFamily: FF, fontVariantNumeric: "tabular-nums" }}>{fmt(c.val)}</div>
              <Delta val={c.val} prev={c.prev} inverse={c.inverse} />
            </div>
          ))}
        </div>
        {totalIng > 0 && (
          <>
            <div style={{ height: 6, background: P.bg, borderRadius: 99, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ height: "100%", borderRadius: 99, background: totalGasto / totalIng > 0.8 ? "#EF4444" : totalGasto / totalIng > 0.6 ? "#F59E0B" : "#10B981", width: `${Math.min((totalGasto / totalIng) * 100, 100)}%`, transition: "width .5s" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>{Math.round((totalGasto / totalIng) * 100)}% del ingreso gastado</span>
              <div>
                <span style={{ fontSize: 13, fontWeight: 800, color: flujoNeto >= 0 ? P.ingresos.base : "#DC2626", fontFamily: FF }}>Flujo neto: {fmt(flujoNeto)}</span>
                <Delta val={flujoNeto} prev={prevFlujoNeto} />
              </div>
            </div>
          </>
        )}
      </Card>

      {/* ═══ 2. ALERTAS DE PRESUPUESTO ═══ */}
      {presupuestos && presupuestos.length > 0 ? (() => {
        const alertas = presupuestos.filter(p => p.cat && p.limite > 0).map(p => {
          const gastado = bycatGasto[p.cat] || 0;
          const pct = gastado / p.limite;
          return { ...p, gastado, pct };
        }).sort((a, b) => b.pct - a.pct);
        const rojos = alertas.filter(a => a.pct >= 1);
        const amarillos = alertas.filter(a => a.pct >= 0.8 && a.pct < 1);
        const verdes = alertas.filter(a => a.pct < 0.8);
        if (rojos.length === 0 && amarillos.length === 0) return null;
        return (
          <Card>
            <Heading>⚠ Presupuestos</Heading>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[...rojos, ...amarillos].map(a => {
                const isRojo = a.pct >= 1;
                return (
                  <div key={a.cat} style={{ padding: "10px 14px", borderRadius: 12, background: isRojo ? "#FEF2F2" : "#FFFBEB", border: `1.5px solid ${isRojo ? "#FECACA" : "#FDE68A"}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: P.text, fontFamily: FF }}>{catIcon(a.cat)} {a.cat}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: isRojo ? "#DC2626" : "#B45309", fontFamily: FF }}>{fmt(a.gastado)} / {fmt(a.limite)}</span>
                    </div>
                    <div style={{ height: 5, background: isRojo ? "#FECACA" : "#FDE68A", borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 99, background: isRojo ? "#DC2626" : "#F59E0B", width: `${Math.min(a.pct * 100, 100)}%` }} />
                    </div>
                    <div style={{ fontSize: 10, color: isRojo ? "#991B1B" : "#92400E", fontFamily: FF, marginTop: 4 }}>
                      {isRojo ? `${fmt(a.gastado - a.limite)} por encima` : `Te quedan ${fmt(a.limite - a.gastado)}`}
                    </div>
                  </div>
                );
              })}
              {verdes.length > 0 && (
                <div style={{ fontSize: 11, color: P.ingresos.base, fontFamily: FF, fontWeight: 600 }}>
                  ✅ {verdes.length} categoría{verdes.length !== 1 ? "s" : ""} dentro de presupuesto
                </div>
              )}
            </div>
          </Card>
        );
      })() : (
        <Card style={{ textAlign: "center", padding: "20px 16px" }}>
          <div style={{ fontSize: 13, color: P.textXs, fontFamily: FF }}>
            🎯 Configura presupuestos por categoría en <strong style={{ color: P.text }}>⚙️ Config</strong> para recibir alertas
          </div>
        </Card>
      )}

      {/* ═══ 3. GASTOS VARIABLES POR CATEGORÍA ═══ */}
      {catEntries.length > 0 && (
        <Card>
          <Heading>Gastos variables por categoría</Heading>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {catEntries.map(([cat, val]) => {
              const pct = totalExtReales > 0 ? (val / totalExtReales) * 100 : 0;
              const color = catColor(cat);
              const prevVal = prevBycat[cat] || 0;
              const isNew = hasPrev && prevVal === 0;
              return (
                <div key={cat}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 14 }}>{catIcon(cat)}</span>
                      <span style={{ fontSize: 12, fontWeight: 500, color: P.textMd, fontFamily: FF }}>{cat}</span>
                      {isNew && <span style={{ fontSize: 8, fontWeight: 700, color: "#7C3AED", background: "#EDE9FE", borderRadius: 99, padding: "1px 6px" }}>NUEVO</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 900, color: P.text, fontFamily: FF }}>{fmt(val)}</span>
                      <Delta val={val} prev={prevVal} inverse />
                    </div>
                  </div>
                  <div style={{ height: 6, background: P.bg, borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: 99, background: color, width: `${Math.min(pct, 100)}%`, transition: "width .5s" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ═══ 4. CAPACIDAD DE GASTO SEMANAL ═══ */}
      {totalIng > 0 && (
        <Card>
          <Heading>Tu ritmo de gasto</Heading>
          {/* Puedes gastar hoy */}
          <div style={{ background: puedesHoy > 0 ? "#ECFDF5" : "#FEF2F2", borderRadius: 14, padding: "14px 18px", marginBottom: 14, border: `1.5px solid ${puedesHoy > 0 ? "#D1FAE5" : "#FECACA"}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: puedesHoy > 0 ? "#065F46" : "#991B1B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: FF }}>Puedes gastar hoy</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: puedesHoy > 0 ? "#059669" : "#DC2626", fontFamily: FF, letterSpacing: "-1px" }}>{fmt(puedesHoy)}</div>
            <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF, marginTop: 4 }}>{diasRestantes} días restantes del mes</div>
          </div>

          {/* Semanas */}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(numSemanas, 4)}, 1fr)`, gap: 8, marginBottom: 14 }}>
            {Array.from({ length: Math.min(numSemanas, 4) }).map((_, i) => {
              const lim = porSemana;
              const gastado = semanaGastos[i];
              const diff = lim - gastado;
              const semActual = Math.ceil(todayDay / 7) - 1;
              const isCurrentOrPast = i <= semActual;
              return (
                <div key={i} style={{ background: !isCurrentOrPast ? P.bg : diff < 0 ? "#FEF2F2" : diff < lim * 0.3 ? "#FFFBEB" : "#ECFDF5", borderRadius: 12, padding: "12px 10px", border: `1.5px solid ${!isCurrentOrPast ? P.border : diff < 0 ? "#FECACA" : diff < lim * 0.3 ? "#FDE68A" : "#D1FAE5"}` }}>
                  <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF, marginBottom: 4 }}>Sem {i + 1}</div>
                  <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>Límite: {fmt(lim)}</div>
                  {isCurrentOrPast ? (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 900, color: P.text, fontFamily: FF, marginTop: 4 }}>{fmt(gastado)}</div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: diff >= 0 ? "#059669" : "#DC2626", fontFamily: FF, marginTop: 2 }}>
                        {diff >= 0 ? `+${fmt(diff)} ✓` : `${fmt(diff)} ✗`}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF, marginTop: 6 }}>Pendiente</div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 16, padding: "12px 16px", background: P.bg, borderRadius: 12 }}>
            <div><div style={{ fontSize: 9, color: P.textXs, fontFamily: FF }}>Por semana</div><div style={{ fontSize: 18, fontWeight: 900, color: P.ingresos.base, fontFamily: FF }}>{fmt(porSemana)}</div></div>
            <div><div style={{ fontSize: 9, color: P.textXs, fontFamily: FF }}>Por día</div><div style={{ fontSize: 18, fontWeight: 900, color: P.textMd, fontFamily: FF }}>{fmt(porDia)}</div></div>
          </div>
        </Card>
      )}

      {/* ═══ 5. MSI COMPROMETIDO ═══ */}
      {msiCompras.length > 0 && (
        <Card>
          <Heading>💳 Meses Sin Intereses</Heading>
          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <div style={{ background: P.fijos.pastel, borderRadius: 12, padding: "10px 14px", flex: 1, border: `1.5px solid ${P.fijos.tint}` }}>
              <div style={{ fontSize: 9, color: P.fijos.text, fontFamily: FF, fontWeight: 700, textTransform: "uppercase" }}>Este mes</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: P.fijos.text, fontFamily: FF }}>{fmt(totalMsiMes)}</div>
            </div>
            <div style={{ background: P.bg, borderRadius: 12, padding: "10px 14px", flex: 1, border: `1.5px solid ${P.border}` }}>
              <div style={{ fontSize: 9, color: P.textXs, fontFamily: FF, fontWeight: 700, textTransform: "uppercase" }}>Total pendiente</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: P.text, fontFamily: FF }}>{fmt(msiCompras.reduce((s, c) => {
                const pagados = Object.values(c.pagos || {}).filter(p => p.pagado).length;
                return s + Math.round(c.totalCompra / c.meses) * (c.meses - pagados);
              }, 0))}</div>
            </div>
          </div>
          {msiCompras.map(c => {
            const cuota = Math.round(c.totalCompra / c.meses);
            const pagados = Object.values(c.pagos || {}).filter(p => p.pagado).length;
            const restantes = c.meses - pagados;
            const finAbsolute = c.mesInicio + c.meses - 1;
            const finMes = finAbsolute % 12;
            const finAnio = (c.anioInicio || year) + Math.floor(finAbsolute / 12);
            return (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${P.border}` }}>
                <div style={{ width: 28, height: 28, borderRadius: 9, background: `${catColor(c.cat)}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>{catIcon(c.cat)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: P.text, fontFamily: FF }}>{c.concepto}</div>
                  <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>{fmt(cuota)}/mes · {restantes} meses restantes · hasta {MESES[finMes]} {finAnio}</div>
                  <div style={{ height: 3, background: P.fijos.tint, borderRadius: 99, marginTop: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: P.fijos.mid, borderRadius: 99, width: `${(pagados / c.meses) * 100}%` }} />
                  </div>
                </div>
                <span style={{ fontSize: 13, fontWeight: 900, color: P.fijos.text, fontFamily: FF, flexShrink: 0 }}>{fmt(cuota)}</span>
              </div>
            );
          })}
        </Card>
      )}

      {/* ═══ 6. TENDENCIA 3 MESES ═══ */}
      {trend.length >= 2 && (
        <Card>
          <Heading>Tendencia: últimos {trend.length} meses</Heading>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${trend.length}, 1fr)`, gap: 10, marginBottom: 14 }}>
            {trend.map((t, i) => {
              const maxGasto = Math.max(...trend.map(x => x.gasto), 1);
              const barH = Math.max((t.gasto / maxGasto) * 60, 4);
              return (
                <div key={t.idx} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: P.textSm, fontFamily: FF, marginBottom: 6 }}>{t.mes}</div>
                  <div style={{ height: 60, display: "flex", alignItems: "flex-end", justifyContent: "center", marginBottom: 4 }}>
                    <div style={{ width: "60%", height: barH, background: P.extras.mid, borderRadius: 6 }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: P.text, fontFamily: FF }}>{fmt(t.gasto)}</div>
                  <div style={{ fontSize: 9, color: "#059669", fontFamily: FF, marginTop: 2 }}>Ahorro: {fmt(t.ahorro)}</div>
                </div>
              );
            })}
          </div>
          {/* Insight automático */}
          {trend.length >= 2 && (() => {
            const last = trend[trend.length - 1];
            const prev = trend[trend.length - 2];
            const gastoSube = last.gasto > prev.gasto;
            const ahorroSube = last.ahorro > prev.ahorro;
            let msg, color;
            if (!gastoSube && ahorroSube) { msg = "Vas por buen camino: gastos bajan y ahorro sube 🟢"; color = "#059669"; }
            else if (!gastoSube && !ahorroSube) { msg = "Gastos bajan pero ahorro también — revisa tus metas ⚠️"; color = "#B45309"; }
            else if (gastoSube && !ahorroSube) { msg = "Cuidado: gastos suben y ahorro baja 🔴"; color = "#DC2626"; }
            else { msg = "Ingresos crecieron — vigila que los gastos no se disparen 🟡"; color = "#B45309"; }
            return (
              <div style={{ padding: "10px 14px", background: P.bg, borderRadius: 10, fontSize: 12, color, fontWeight: 600, fontFamily: FF }}>
                {msg}
              </div>
            );
          })()}
        </Card>
      )}

      {/* ═══ 7. INSIGHTS ═══ */}
      {insights.length > 0 && (
        <Card>
          <Heading>Observaciones del mes</Heading>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {insights.map((text, i) => (
              <div key={i} style={{ fontSize: 13, color: P.textMd, fontFamily: FF, lineHeight: 1.5, padding: "8px 12px", background: P.bg, borderRadius: 10 }}>
                {text}
              </div>
            ))}
          </div>
        </Card>
      )}

    </div>
  );
}
